import React from 'react';
import { styled } from '@mui/material/styles';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CardContent} from '@mui/material';
// PDF Download Library
import 'jspdf-autotable';
import { formatDollarFull } from '../utilityFunctions';

// Styled components for consistent table styling
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: "Titillium Web, sans-serif",
    fontSize: '14px',
    padding: '3px 16px', // Padding for individual cells
    '&.table-header': {
        fontWeight: 'bold',
        backgroundColor: '#083D61',
        color: 'white',
        padding: '10px 16px', // Padding for Row Headers
    },
    '&.red-text': {
        color: 'red',
        fontSize: 12,
        whiteSpace: 'nowrap',
        padding: '2.5px 9px',
    },
    '&.green-text': {
        color: 'green',
        fontSize: 12,
        whiteSpace: 'nowrap',
        padding: '2.5px 9px',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: '20px',
    '&:nth-of-type(odd)': {
        backgroundColor: '#f9f9f9',
    },
    '&:nth-of-type(even)': {
        backgroundColor: '#ffffff',
    },
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
}));

const StyledTable = styled(Table)({
    minWidth: 500,
    whiteSpace: 'nowrap',
    fontFamily: "Titillium Web, sans-serif",
});

const calculatePaymentAge = (invoiceDate, payFullDate) => {
    if (!invoiceDate || !payFullDate) {
        return null;
    }
    const invoice = new Date(invoiceDate);
    const paid = new Date(payFullDate);

    if (isNaN(invoice) || isNaN(paid)) {
        return null;
    }

    const timeDiff = paid - invoice;
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24)); 
}

const calculateAveragePaymentAge = (invoiceData) => {
    if (!invoiceData || invoiceData.length === 0) {
        return 0;
    }

    const totalPaymentAge = invoiceData.reduce((acc, invoice) => acc + (invoice.pmt_age || 0), 0);
    return (totalPaymentAge / invoiceData.length).toFixed(2);
};


const PaymentsAndInvoicingTable = ({ invoiceData, isModal, selectedProject }) => {

    const averagePaymentAge = calculateAveragePaymentAge(invoiceData);

    if (!invoiceData || invoiceData.length === 0) {
        return (
            <CardContent sx={{ height: '200px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ textAlign: 'center' }}>--</Typography>
            </CardContent>
        );
    }

    const sortedInvoiceData= invoiceData
    // const sortedInvoiceData = [...invoiceData].sort(
    //     (a, b) => new Date(b.invoice_date) - new Date(a.invoice_date)
    //   );

    return (
        <CardContent>
            <TableContainer 
                component={Paper} 
                sx={{ 
                    maxHeight: isModal ? '100%' : '250px',
                    overflowX: 'auto'
                }}
            >
                <StyledTable aria-label="owner billings table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell className="table-header">Invoice #</StyledTableCell>
                            <StyledTableCell className="table-header">Description</StyledTableCell>
                            <StyledTableCell className="table-header">Invoice Date</StyledTableCell>
                            <StyledTableCell className="table-header">Date Paid</StyledTableCell>
                            <StyledTableCell className="table-header" sx={{textAlign: 'center'}}>Payment Age</StyledTableCell> 
                            <StyledTableCell className="table-header" sx={{textAlign: 'right'}}>Invoiced</StyledTableCell>
                            <StyledTableCell className="table-header" sx={{textAlign: 'right'}}>Retainage</StyledTableCell>
                            <StyledTableCell className="table-header" sx={{textAlign: 'right'}}>Paid</StyledTableCell>
                            <StyledTableCell className="table-header" sx={{textAlign: 'right'}}>Cumulative Paid</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedInvoiceData.map((row, index) => {
                            return (
                                <StyledTableRow key={index}>
                                    <StyledTableCell component="th" scope="row">
                                        {row.invoice ? row.invoice.replace(/\s+/g, '') : '--'}
                                    </StyledTableCell>
                                    <StyledTableCell 
                                        title={row.pay_app_description || '--'}
                                        sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    >
                                        {row.pay_app_description || '--'}
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        {row.invoice_date ? row.invoice_date : '--'}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {row.pay_full_date ? row.pay_full_date : '--'} 
                                    </StyledTableCell>
                                    <StyledTableCell sx={{textAlign: 'center'}}>
                                        {((row.pmt_age !== null)&&(row.pmt_age !== '-')) ? (
                                            <Typography 
                                                sx={{ 
                                                    color: row.pmt_age >= 30 ? 'red' : 'green',
                                                    fontSize: 12,
                                                    whiteSpace: 'nowrap',
                                                    padding: '2.5px 9px',
                                                    fontFamily: "Titillium Web, sans-serif"
                                                }}
                                            >
                                                {row.pmt_age} days
                                            </Typography>
                                        ) : (
                                            '--'
                                        )} 
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ color: row.invoiced < 0 ? 'red' : 'inherit', textAlign: 'right' }}>
                                        {row.invoiced ? formatDollarFull(row.invoiced) : '--'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ color: row.invoiced < 0 ? 'red' : 'inherit', textAlign: 'right' }}>
                                        {row.retainage ? formatDollarFull(row.retainage) : '--'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ color: row.paid < 0 ? 'red' : 'inherit', textAlign: 'right' }}>
                                        {row.paid ? formatDollarFull(row.paid) : '--'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{textAlign: 'right'}}>
                                        {row.cumulative_paid ? formatDollarFull(row.cumulative_paid) : '--'}
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </CardContent>
    );
};

export default PaymentsAndInvoicingTable;